import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';

import type { HelpSection } from '../../../../types/Cashier';
import { CardType } from '../../../../types/cardType';
import CTAButton from '../../../CTAButton/CTAButton';
import Container from '../../../Container/Container';
import SummaryMessages from '../../../SummaryMessages/SummaryMessages';
import HelpSectionContent from '../DepositSummary/components/HelpSectionContent/HelpSectionContent';

import {
  TXTCLI_Summary,
  TXTWCL_Confirm_Deposit,
  TXTWCL_Help_with_deposits,
  TXTWCL_LetXs_just_check_if_this_is_co_ELL,
  TXTWCL_Make_payment
} from './DepositSummary.messages.json';
import AmountSummary from './components/AmountSummary/AmountSummary';
import ApplePay from './components/ApplePay/ApplePay';
import GooglePay from './components/GooglePay/GooglePay';
import InfoMessages from './components/InfoMessages/InfoMessages';
import PaymentMethodSummary from './components/PaymentMethodSummary/PaymentMethodSummary';

const DepositSummary: React.FC = () => {
  const isSwcPaypalSandboxQA = useFeature(FeatureFlag.SwcPaypalSandboxQA);
  const {
    data: {
      paySystemDetails: { type, info }
    },
    values: {
      deposit: { fraudnetMerSessionId }
    }
  } = useWizardContext();
  const isSavedPayPal = !!(
    type === CardType.PayPal &&
    fraudnetMerSessionId &&
    info?.fraudnetSrcWebId &&
    info?.supportBillingAgreement
  );
  const [scriptLoading, setScriptLoading] = useState(isSavedPayPal);
  const isManageDepositLimitsEnabled = useFeature(FeatureFlag.SwcManageDepositLimits);
  const { jumpBack } = useWizardJump();

  const customSubmitButton: Record<number, JSX.Element> = {
    [CardType.ApplePay]: <ApplePay />,
    [CardType.GooglePay]: <GooglePay />
  };

  const label = [CardType.PsPayAstropay, CardType.PsPayInovapay, CardType.BoletoAstropay].includes(
    type
  )
    ? TXTWCL_Make_payment
    : TXTWCL_Confirm_Deposit;

  const helpSection: HelpSection = {
    title: TXTWCL_Help_with_deposits,
    body: <HelpSectionContent />
  };

  const submitButton = customSubmitButton[type] || <CTAButton translation={label} />;

  useEffect(() => {
    if (isSavedPayPal) {
      const jsonPart = document.createElement('script');
      const jsPart = document.createElement('script');
      jsonPart.type = 'application/json';
      jsonPart.text = JSON.stringify({
        f: fraudnetMerSessionId,
        s: info.fraudnetSrcWebId,
        sandbox: isSwcPaypalSandboxQA
      });
      jsonPart.setAttribute('fncls', 'fnparams-dede7cc5-15fd-4c75-a9f4-36c430ee3a99');
      document.body.appendChild(jsonPart);

      jsPart.type = 'text/javascript';
      jsPart.src = `https://c.paypal.com/da/r/fb.js`;
      jsPart.onload = () => setScriptLoading(false);
      document.body.appendChild(jsPart);

      return () => {
        document.body.removeChild(jsonPart);
        document.body.removeChild(jsPart);
      };
    }
  }, [fraudnetMerSessionId, info?.fraudnetSrcWebId, isSavedPayPal, isSwcPaypalSandboxQA, type]);

  const onBackClick = () => jumpBack('#deposit.deposit-amount');

  return (
    <Container
      page={DepositSummary}
      headerProps={{
        title: <Translation {...TXTCLI_Summary} />,
        buttons: { back: true, onBackClick }
      }}
      pageTopTitle={<Translation {...TXTWCL_LetXs_just_check_if_this_is_co_ELL} />}
      loading={scriptLoading}
      helpSupportResolver
      showFooter
      helpSection={helpSection}
      limitsSection={isManageDepositLimitsEnabled}
    >
      <PaymentMethodSummary />
      <SummaryMessages type={type} />
      <AmountSummary />
      <InfoMessages />
      {submitButton}
    </Container>
  );
};

DepositSummary.displayName = 'DepositSummary';

export default DepositSummary;
